import { ref } from 'vue';
import type { BaseContent, Content, NewContent } from '@ilteducation/content-registry-types';
import type { Data, NewData } from '@ilteducation/content-ref-data-types';
import { BaseState } from '@ilteducation/content-registry-types/enums/content-enums';
import mediaProvider, { RemoveParams } from '@/data-provider/mediaProvider';
import { Exercise, UnknownExercise } from '@ilteducation/exercise-service-types';

type InputTypes = Content | NewContent | Data | NewData | Exercise | UnknownExercise | undefined;

function isContentType(content: InputTypes): content is Content {
  return (content as Content).id !== undefined;
}

const useMediaFormFeature = (item: InputTypes) => {
  // const isCloneMode = ref(false);
  const isFormEdited = ref(false);
  const toggleStateInProgress = ref(false);
  const uploadInProgress = ref(false);
  const removeInProgress = ref(false);

  const isCloneMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!urlParams.get('source');
  };

  const saveButtonAvailable = () => {
    if (isCloneMode()) {
      return true;
    }
    // Save button is active if form is in "edit" mode or its data has been changed
    return isFormEdited.value || !item;
  };

  const publishButtonAvailable = () => {
    if (isCloneMode()) {
      return false;
    }

    if (item && isContentType(item)) {
      return item.state ? item.state === BaseState.Published || item.state === BaseState.Disabled : false;
    }
    return false;
  };

  const mounted = () => {
    isFormEdited.value = false;
  };

  const uploadMedia = async (id: string, data: File) => {
    uploadInProgress.value = true;
    await mediaProvider.uploadMedia(id, data);
    uploadInProgress.value = false;
  };

  const toggleMediaState = async () => {
    if (item && isContentType(item)) {
      const isPublished = item?.state === BaseState.Published;
      toggleStateInProgress.value = true;
      await mediaProvider
        .toggleMediaState(item?.id, isPublished)
        .then(() => {
          // eslint-disable-next-line no-param-reassign
          item.state = isPublished ? BaseState.Disabled : BaseState.Published;
        })
        .then(() => {
          toggleStateInProgress.value = false;
        });
    }
  };

  const removeMedia = async (params: RemoveParams) => {
    if (item && isContentType(item)) {
      const isPublished = item?.state === BaseState.Published;
      removeInProgress.value = true;
      await mediaProvider.removeMedia(item?.id, params).finally(() => {
        removeInProgress.value = false;
      });
    }
  };

  const getStateColor = () => {
    // eslint-disable-next-line no-prototype-builtins
    if (item && item.hasOwnProperty('state') && 'state' in item) {
      switch (item.state as BaseState) {
        case BaseState.Published:
          return 'green';
        case BaseState.Disabled:
          return 'red';
        default:
          return 'yellow';
      }
    }
    return 'yellow';
  };

  return {
    saveButtonAvailable,
    mounted,
    uploadMedia,
    getStateColor,
    publishButtonAvailable,
    toggleMediaState,
    toggleStateInProgress,
    isCloneMode,
    isFormEdited,
    removeMedia,
  };
};

export default useMediaFormFeature;
